import { ChangeEventHandler, forwardRef, useCallback, useRef, useState } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { Icon, SvgIcon, useFieldContext } from '@bridebook/aura';
import { cn } from '../../lib/utils/cn';
import { ClearButton } from './clear-button';
import {
  inputBaseLayout,
  inputBaseTypography,
  inputBaseTransition,
  inputBaseRing,
  inputErrorVariant,
  inputDisabledVariant,
  inputWithLeftElementVariant,
  inputWithRightElementVariant,
} from './input-variants';

const inputVariants = cva(
  cn(
    inputBaseLayout,
    inputBaseTypography,
    'placeholder:text-space-60',
    inputBaseTransition,
    'disabled:cursor-not-allowed',
    inputBaseRing,
    'focus:bg-white focus:ring-2',
  ),
  {
    variants: {
      variant: {
        default: 'bg-space-04 group-hover:data-[error=false]:not-focus:not-disabled:bg-space-08',
        white:
          'border border-space-40 bg-white focus:border-transparent group-hover:data-[error=false]:not-focus:not-disabled:bg-space-04',
      },
      error: inputErrorVariant,
      disabled: inputDisabledVariant,
      withLeftElement: inputWithLeftElementVariant,
      withRightElement: inputWithRightElementVariant,
    },
    defaultVariants: {
      error: false,
      disabled: false,
      withRightElement: false,
      variant: 'default',
    },
  },
);

export interface InputProps
  extends Omit<React.ComponentPropsWithoutRef<'input'>, 'disabled' | 'size'>,
    Omit<VariantProps<typeof inputVariants>, 'withLeftElement' | 'withRightElement'> {
  classNames?: {
    wrapper?: string;
    icon?: string;
  };
  /**
   * Icon to display on the left side of the input.
   */
  icon?: SvgIcon;
  /**
   * Shows a clear button when the input has a value.
   */
  showClearBtn?: boolean;
  /**
   * Additional event handler for the clear button click.
   */
  onClear?: (event: React.MouseEvent<HTMLButtonElement>) => void;
}

const hasInputValue = (value: React.ComponentPropsWithoutRef<'input'>['value']) => {
  if (Array.isArray(value)) {
    return value.length > 0;
  }

  if (value === undefined || value === null) {
    return false;
  }

  return String(value).length > 0;
};

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      variant = 'default',
      error: errorProp,
      disabled: disabledProp,
      classNames,
      showClearBtn,
      icon,
      onClear,
      ...props
    },
    ref,
  ) => {
    const { value, defaultValue, onChange, ...inputProps } = props;

    const context = useFieldContext();
    const disabled = disabledProp ?? context?.disabled ?? false;
    const error = errorProp ?? context?.error ?? false;
    const isControlled = value !== undefined;
    const [uncontrolledHasValue, setUncontrolledHasValue] = useState(() =>
      hasInputValue(defaultValue),
    );
    const hasValue = isControlled ? hasInputValue(value) : uncontrolledHasValue;

    const inputRef = useRef<HTMLInputElement | null>(null);

    const setRefs = useCallback(
      (node: HTMLInputElement | null) => {
        inputRef.current = node;
        if (typeof ref === 'function') {
          ref(node);
        } else if (ref) {
          ref.current = node;
        }
      },
      [ref],
    );

    const setInputValue = (input: HTMLInputElement, nextValue: string) => {
      // Use the native setter so React sees this as a real DOM value change.
      // This keeps controlled and uncontrolled usages consistent.
      const valueSetter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')?.set;
      if (valueSetter) {
        valueSetter.call(input, nextValue);
      } else {
        input.value = nextValue;
      }
    };

    const clearInputValue = (event: React.MouseEvent<HTMLButtonElement>) => {
      const input = inputRef.current;
      if (!input || disabled) {
        return;
      }

      onClear?.(event);

      setInputValue(input, '');

      // Fire an input event so React onChange handlers run.
      // In controlled mode, parent state updates from this event.
      input.dispatchEvent(new Event('input', { bubbles: true }));

      input.focus();
    };

    const handleChange: ChangeEventHandler<HTMLInputElement> = (event) => {
      if (!isControlled) {
        setUncontrolledHasValue(event.target.value.length > 0);
      }
      onChange?.(event);
    };

    const shouldShowClearBtn = !!showClearBtn && hasValue;

    return (
      // This extra div is needed to apply the hover state to the input in a way that is not triggered by focusing the label.
      <div className={cn('group relative', classNames?.wrapper)}>
        {icon && (
          <Icon
            icon={icon}
            className={cn(
              'pointer-events-none absolute top-1/2 left-4 z-10 size-[16px] -translate-y-1/2 text-space',
              classNames?.icon,
            )}
          />
        )}
        <input
          ref={setRefs}
          data-error={error ? 'true' : 'false'}
          disabled={disabled ?? false}
          className={cn(
            inputVariants({
              error,
              disabled: disabled ?? false,
              withLeftElement: !!icon,
              withRightElement: shouldShowClearBtn,
              variant,
            }),
            className,
          )}
          value={value}
          defaultValue={defaultValue}
          onChange={handleChange}
          {...inputProps}
        />
        {shouldShowClearBtn && <ClearButton disabled={disabled} onClick={clearInputValue} />}
      </div>
    );
  },
);

Input.displayName = 'Input';
